import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import ReactJSON from 'react-json-view';
import { toast } from 'react-toastify';

import { makeStyles, useTheme } from '@mui/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Input from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Fab from '@mui/material/Fab';
import FavoriteBorder from '@mui/icons-material/FavoriteBorder';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';

import TransformIcon from '@mui/icons-material/Transform';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import ShareIcon from '@mui/icons-material/Share';
import TuneIcon from '@mui/icons-material/Tune';
import Favorite from '@mui/icons-material/Favorite';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import NavigateBeforeIcon from '@mui/icons-material/KeyboardArrowUp';
import NavigateNextIcon from '@mui/icons-material/ExpandMore';
import Popover from '@mui/material/Popover';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import HuntIcon from '@mui/icons-material/TrackChanges';

import StyledPaper from '../../../components/styled/paper';
import Tooltip from '../../../components/styled/tooltip';
import KeyValuePair from '../../../components/keyvaluepair';
import SensorName from '../../../components/sensorname';
import TabPanel from '../../../components/tabpanel';
import { TagSimpleList } from '../../../components/tags/taglist';
import Tag from '../../../components/tags/tag';
import SeaUrchin from '../../../components/charts/seaurchin';
import IPAddress from '../../../components/ipaddress';
import PopupMenu from '../../../components/popupmenu';
import LabeledToolbar from '../../../components/styled/toolbar';
import BTJobName from '../../../components/bt-jobs';
import ProgressBar from '../../../components/progress-bar';

import { Flatten, MtdtMerge, SaveFile, ToArray, IsEmpty, GetBTUUID } from '../../../libraries/helpers';
import useWindowSize from '../../../libraries/usewindowsize';

import MetadataService from '../../../data/metadata-service';
import AlertsService from '../../../data/alerts-service';
import Auth, { ProfileContext, ParamsSerializer } from '../../../data/auth-service';
import SensorsService from '../../../data/sensors-service.js';
import DataContext from '../../../data/data-context.js';
import PolicyService from '../../../data/policy-service.js';
import PcapService from '../../../data/pcap-service.js';
import BacktestingService from '../../../data/backtesting-service.js';

import {
    get as _get,
    set as _set,
    mergeWith as _mergeWith,
    cloneDeep as _cloneDeep,
    isEmpty as _isEmpty, // collections test
} from 'lodash';
import Moment from 'moment';
// import { useWhatChangedEffect } from '../../../libraries/withwhyrender';

const useStyles = makeStyles(theme => ({
    root: {
        width: 'calc(100% - 10px)',
        padding: 5,
        position: 'absolute',
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[5],

        '& .CodeMirror-scroll': {
            maxHeight: 600,
        },
    },
    alertHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: theme.spacing(2),
        paddingRight: 0,
    },
    tagsToolbar: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        direction: 'rtl',
    },
    box: {
        overflowY: 'scroll',
        msOverflowStyle: 'none',
        '& code': {
            fontSize: '15px',
        },
        '&::-webkit-scrollbar': {
            display: 'none',
        },
    },
    float: {
        float: 'right',
        position: 'relative',
        zIndex: '10000',
    },
    rawJson: {
        margin: 0,
        whiteSpace: 'pre-wrap',
        wordBreak: 'normal',
    },
    justifySpace: {
        justifyContent: 'space-between',
    },
    tabToolbar: {
        justifyContent: 'flex-end',
    },
    autoColumnList: {
        overflowY: 'scroll',
        paddingTop: 0,
        paddingBottom: 0,

        '& ul': {
            columns: '400px auto',
            listStyle: 'none',
            padding: 0,
            marginTop: 0,
            marginBottom: 0,

            '& li': {
                padding: 0,
                height: 'fit-content',
                breakInside: 'avoid',
            }
        },

        // nope: can't save people from themselves
        // hide ugly windows scrollbar
        // https://stackoverflow.com/a/38994837/5895550
        // '-ms-overflow-style': 'none', /* Internet Explorer 10+ */
        // scrollbarWidth: 'none', /* Firefox */
        //
        // '&::-webkit-scrollbar': {
        //     display: 'none', /* Safari and Chrome */
        // }
    },
    inlineTag: {
        display: 'inline-block',
        verticalAlign: 'middle',
        direction: 'ltr', // don't reverse the popups
    },
    margin: {
        margin: theme.spacing(1),
    },
    itemIcon: {
        minWidth: 25,
    },
    mapped: {
        verticalAlign: 'middle',
        paddingRight: 5,
    },
    signature: {
        ...theme.typography.h6,
        cursor: 'pointer',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
}));

const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

const MetadataDetail = (props) => {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();

    const { updateProfile, getProfile } = useContext(ProfileContext);
    const { sensorNames, mtdtMappings: mappings, backtestingJobs } = useContext(DataContext);
    const toolbarRef = useRef(null);
    const { height } = useWindowSize();

    const alertsPerms = Auth.permissions.get('Alerts');
    const metadataPerms = Auth.permissions.get('Metadata');
    const gatorPerms = Auth.permissions.get('DataTuningPolicy');
    const pcapPerms = Auth.permissions.get('Pcap');
    const sensorPerms = Auth.permissions.get('Sensors');

    const [showFavorites, setShowFavorites] = useState(false);
    const [favorites, setFavorites] = useState({});
    const [jsonFormatted, setJSONFormatted] = useState(true);
    const [cheight, setCHeight] = useState(300);
    const [search, setSearch] = useState('');
    const [showTags, setShowTags] = useState(false);
    const [tab, setTab] = useState(0);
    const [valuePopup, setValuePopup] = useState();
    const [policy, setPolicy] = useState({});
    const [pcapSituation, setPcapSituation] = useState({});
    const [backtesting, setBacktesting] = useState();
    const [alertsLookup, setAlertsLookup] = useState();
    const [synonyms, setSynonyms] = useState({});

    const [tabs, setTabs] = useState({});

    const field = k => MetadataService.fieldsMap[k] || MetadataService.fieldsMap[`_source.${k}`] || {};

    const skip = path => !(field(path).btShow) && field(path).jsonHidden;

    const getSort = path => field(path).jsonSort || 1111;

    const isFavorite = (path) => favorites[path];

    const handleJSONFormat = () => {
        setJSONFormatted(!jsonFormatted);
    };

    const handleSaveJson = () => {
        const json = tabs['Raw JSON'].data || {};
        const sensor = sensorNames[_get(props.mtdt, '_source.bricata.sensor_uuid')];
        if (sensor) json.sensor = sensor.name || sensor.host;
        delete json.count;
        SaveFile(
            JSON.stringify(json),
            `${_get(props.mtdt, '_source.bricata.event_uuid')}.json`,
            'text/json;charset=utf-8;',
        );
    };

    const handleToggleFavorite = (path) => {
        const profile = getProfile('metadata');
        const f = favorites;
        if (f[path]) delete f[path];
        else f[path] = true;

        setFavorites({ ...f });
        updateProfile('metadata', { ...profile, favorites: { show: showFavorites, selections: f } });
    };

    const handleSearchChanged = event => {
        setSearch(event.target.value || '');
    };

    const handleShowFaboritesToggle = () => {
        setShowFavorites(!showFavorites);
        const profile = getProfile('metadata');
        updateProfile('metadata', _set(profile, 'favorites.show', !showFavorites));
    };

    const getPcapMenu = useCallback(() => {
        const menu = [];
        if (pcapSituation.full) menu.push('Request full Pcap');
        if (pcapSituation.smart) menu.push('Request Smart Pcap');
        return menu;
    }, [pcapSituation]);

    const handlePcap = type => {
        const sensor = sensorNames[_get(props.mtdt, '_source.bricata.sensor_uuid')];
        const host = sensor ? sensor.host : _get(props.mtdt, '_source.bricata.sensor_ipv4');

        const params = PcapService.mtdt2filter(props.mtdt);
        if (type === 'Request Smart Pcap') {
            PcapService.create(host, params, props.cmc)
                .then(() => {
                    const msg = MC2CHECK
                        ? 'Check the PCAP Request page for the results'
                        : 'You will be notified when your PCAP is ready';
                    toast.info(msg, { hideProgressBar: true });
                }, err => {
                    console.error(err);
                    toast.error('There was an error issuing a PCAP request', { hideProgressBar: true });
                });
            return;
        }
        navigate(`/views/packet_requests/create_pcap?${ParamsSerializer({ ...params, host })}`);
    };

    const handleTune = () => {
        if (!policy.gator) return;

        const summary = tabs.summary;
        if (!summary || !summary.data) return;

        const nodes = [];
        // don't add empty nodes
        if (summary.data['bro_log.proto']) {
            nodes.push({
                ...MetadataService.fieldsMap['_source.bro_log.proto'],
                value: summary.data['bro_log.proto'],
            });
        }
        if (summary.data['bro_log.id.orig_h']) {
            nodes.push({
                ...MetadataService.fieldsMap['_source.bro_log.id.orig_h'],
                value: summary.data['bro_log.id.orig_h'],
            });
        }
        if (summary.data['bro_log.id.resp_h']) {
            nodes.push({
                ...MetadataService.fieldsMap['_source.bro_log.id.resp_h'],
                value: `${summary.data['bro_log.id.resp_h']}`,
            });
        }
        if (summary.data['bro_log.id.orig_p']) {
            nodes.push({
                ...MetadataService.fieldsMap['_source.bro_log.id.orig_p'],
                value: `${summary.data['bro_log.id.orig_p']}`,
            });
        }
        if (summary.data['bro_log.id.resp_p']) {
            nodes.push({
                ...MetadataService.fieldsMap['_source.bro_log.id.resp_p'],
                value: `${summary.data['bro_log.id.resp_p']}`,
            });
        }
        props.onTune({ nodes }, policy.gator);
    };

    const fetchAssociatedAlerts = useCallback(summary => {
        const flows = ToArray(_get(summary, 'bricata.flow_uuid', []));
        if (flows.length < 1) return;

        let start = _get(props.mtdt, '_source.timestamp');
        let end = _get(summary, 'end_time') || start;
        start = Moment(start).subtract(24, 'hour').utc().toISOString();
        end = Moment(end).add(24, 'hour').utc().toISOString();

        const filter = {
            operator: 'Or',
            nodes: flows.map(f => ({ variable: 'flow_uuid', value: f })),
        };
        AlertsService.getData({ start, end, filter, cmc: props.cmc }).then(res => {
            if (res && res.data && res.data.length > 0) {
                setAlertsLookup({ start, end, flows });
            }
        });
    }, [props.mtdt, props.cmc]);

    const handleFindAlerts = () => {
        if (!alertsLookup) return;

        const filter = {
            operator: 'Or',
            nodes: alertsLookup.flows.map(f => ({ variable: 'flow_uuid', value: f }))
        };
        const p = {
            start: alertsLookup.start,
            end: alertsLookup.end,
            filter: JSON.stringify(filter),
            cmc: (props.cmc || {}).uuid,
        };
        navigate(`/views/alerts?${ParamsSerializer(p)}`);
    };

    const getTabIndex = t => {
        const tl = (Object.keys(tabs) || [])
            .sort((a, b) => tabs[a].order - tabs[b].order);

        return tl.findIndex(tab => tab === t);
    };

    const handleTabChanged = (e, v) => setTab(v);

    const handleTag = (data, t, label) => {
        let uuids = data['bricata.event_uuid'];
        uuids = Array.isArray(uuids) ? uuids : [uuids];
        props.onTag && props.onTag(uuids, t.name);

        // update the summary tab
        const summary = tabs.summary;
        if (summary) {
            doTag(summary.data, t);
        }
        // if this is the summary tab, update all the other tabs as well
        if (label === 'summary') {
            Object.entries(tabs || {})
                .filter(([k, v]) => k !== 'Raw JSON' && k !== 'Activity Flow' && k !== 'summary')
                .forEach(([k, v]) => doTag(v.data, t));
        } else {
            doTag(data, t);
        }
        // kick the view
        setTabs({ ...tabs });
    };

    const handleUntag = (data, t, label) => {
        let uuids = data['bricata.event_uuid'];
        uuids = Array.isArray(uuids) ? uuids : [uuids];
        props.onUntag && props.onUntag(uuids, t);

        // update visible rows immediately
        // update the summary tab
        const summary = tabs.summary;
        if (summary) {
            doUntag(summary.data, t);
        }
        // if this is the summary tab, update all the other tabs as well
        if (label === 'summary') {
            Object.entries(tabs || {})
                .filter(([k, v]) => k !== 'Raw JSON' && k !== 'Activity Flow' && k !== 'summary')
                .forEach(([k, v]) => doUntag(v.data, t));
        } else {
            doUntag(data, t);
        }
        // kick the view
        setTabs({ ...tabs });
    };

    const doTag = (data, t) => {
        if (!data['bricata.tag']) data['bricata.tag'] = [];
        if (!data['bricata.tag'].find(tag => tag === t.name)) {
            data['bricata.tag'].unshift(t.name);
        }
    };

    const doUntag = (data, t) => {
        if (!data['bricata.tag']) return;
        const i = (data['bricata.tag'] || []).findIndex(tag => t === tag);
        if (i >= 0) data['bricata.tag'].splice(i, 1);
    };

    const isIndexed = k => {
        const f = field(k);
        return f && f.type !== 'date' && f.indexed;
    };

    const getType = k => {
        const f = field(k);
        return (f || {}).type;
    };

    const getTitle = useCallback(k => {
        const f = synonyms[k] || field(k);
        return (f || {}).title;
    }, [synonyms]);

    // get's mapped value
    const getValue = useCallback((k, v) => {
        const f = field(k);
        const m = mappings[f.variable];
        if (!m) return v;

        let ret = m.reduce((a, c) => {
            const cv = MetadataService.value(props.mtdt, MetadataService.columnsMap[c]);
            return [
                ...a,
                ...ToArray(cv)
            ];
        }, []).filter(a => !IsEmpty(a, true));
        ret = ret.length === 1 ? ret[0] : ret;
        return ret;
    }, [props.mtdt, mappings]);

    // returns tip for mapped columns, single value or array
    const getMappedTip = useCallback((k) => {
        const f = field(k);
        const m = mappings[f.variable];
        if (!m) return '';

        let ret = m.reduce((a, c) => {
            const cv = MetadataService.value(props.mtdt, MetadataService.columnsMap[c]);
            return [
                ...a,
                ...ToArray(cv).map(value => ({ key: getTitle(c), value }))
            ];
        }, []).filter(a => !IsEmpty(a.value, true)).map(a => a.key);
        ret = ret.length === 1 ? ret[0] : ret;
        return ret;
    }, [props.mtdt, mappings, getTitle]);

    const getTitleIcon = useCallback(k => {
        const f = field(k);
        if (mappings[f.variable] && (mappings[f.variable].length > 1 || mappings[f.variable][0] !== f.variable)) {
            const ms = mappings[f.variable].map(m => `${MetadataService.columnsMap[m].title}`);
            return (<Tooltip
                arrow placement="top"
                size="small"
                title={ms.join(', ')}>
                <TransformIcon color='primary' className={classes.mapped} size='small'/>
            </Tooltip>);
        }
        return undefined;
    }, [mappings, classes]);

    const addSynonym = (k, delimiter, depth) => {
        const a = k.split(delimiter);
        if (a.length > 1) {
            const dk = a.slice(depth - 1).join('-');
            if (synonyms[dk]) {
                console.log('we need to go deeper', k, synonyms);
                return addSynonym(k, delimiter, depth - 1);
            }
            setSynonyms(current => ({
                ...current,
                [k]: { title: dk }
            }));
        }
    };

    const disambiguate = ({ object, delimiter }) => {
        const flat = Flatten({ object, delimiter });
        const m = {};
        const keys = Object.keys(flat).filter(k => !skip(k) && !getTitle(k));
        keys.forEach((k, i) => {
            const title = (k || '').split(delimiter).slice(-1).pop();
            if (m[title]) {
                addSynonym(k, delimiter, -1);
                const og = keys.find(kk => (kk || '')
                    .split(delimiter)
                    .slice(-1).pop() === title);
                addSynonym(og, delimiter, -1);
            }
            m[title] = true;
        });
    };

    const isIP = k => {
        const f = field(k);
        return (f || {}).type === 'ip';
    };

    const isFileHash = k => {
        const f = field(k);
        return (f || {}).type === 'file_hash';
    };

    const getCountry = k => {
        if (k === 'bro_log.id.orig_h') return _get(props.mtdt, '_source.bricata.src_location.country');
        if (k === 'bro_log.id.resp_h') return _get(props.mtdt, '_source.bricata.dest_location.country');
    };

    const handleOnValueClick = (e, variable, value) => {
        setValuePopup({ elm: e, variable, value });
    };

    const handleOnFilter = e => {
        if (!props.onFilter || !valuePopup) return;
        let value = valuePopup.value;
        const f = field(value);

        // pcr needs to be converted to a range
        if (valuePopup.variable === 'bro_log.pcr') {
            if (isNaN(value)) {
                console.log('expecting pcr to be a number', value);
                return;
            }
            value = `${value}:${value}`;
        }

        f && props.onFilter([{
            variable: valuePopup.variable,
            value,
            type: f.type
        }]);
        setValuePopup();
    };

    const uuid = d => _get(d, 'bricata.event_uuid', 0);

    const getUniqueName = (name, names) => {
        let i = 0;
        let u = name;
        while (names.find(n => n === u)) {
            i++;
            u = `${name} ${i}`;
        }
        return u;
    };

    const renderTab = useCallback((k, v) => {
        switch (k) {
            case 'Activity Flow': return (
                <React.Fragment>
                    <h3>
                        <IPAddress country={v.data.country} address={v.data.ip} />
                    </h3>
                    <h3>
                        <span style={{ paddingLeft: 15 }}>Top Traffic</span>
                    </h3>

                    <SeaUrchin
                        ip={v.data.ip}
                        params={props.params}
                        onFilter={f => props.onFilter([f])}
                        showhelp={true}/>
                </React.Fragment>
            );
            case 'Backtesting': return (
                <React.Fragment>
                    <Box p={1} display="flex" style={{ paddingBottom: 0 }}>
                        {/* job name */}
                        {!props.onFilter && <Typography className={classes.signature} variant="h6">
                            <BTJobName uuid={v.data.uuid}/>
                        </Typography>}

                        {props.onFilter && v.data.uuid && <Link
                            color="primary"
                            onClick={e => handleOnValueClick(e.currentTarget, 'bt_uuid', v.data.uuid)}
                            className={classes.signature}>
                            <BTJobName uuid={v.data.uuid}/>
                        </Link>}

                        {/* jobs progress */}
                        {_get(backtesting, 'data.progress') !== undefined &&
                            <div style={{ width: 250, marginLeft: 20 }}>
                                <ProgressBar value={backtesting['data.progress']}/>
                            </div>
                        }
                    </Box>

                    {/* file name */}
                    {_get(props.mtdt, '_source.bricata.bt_pcap_name') &&
                        <Box style={{ marginBottom: 15 }}>
                            <KeyValuePair
                                title="PCAP File Name"
                                type="str"
                                path={'bricata.bt_pcap_name'}
                                data={_get(props.mtdt, '_source.bricata.bt_pcap_name')}/>
                        </Box>
                    }

                    <Box p={1} style={{ maxHeight: cheight }} className={classes.autoColumnList}>
                        <ul>
                            {Object.entries(backtesting || {})
                                .filter(([k]) => !skip(`backtesting.${k}`))
                                .map(([k, v], i) =>
                                    <li key={`${k}_${i}`}>
                                        <KeyValuePair
                                            title={getTitle(`backtesting.${k}`)}
                                            type={getType(`backtesting.${k}`)}
                                            fileHash={isFileHash(`backtesting.${k}`)}
                                            path={k}
                                            data={v}/>
                                    </li>
                                )}
                        </ul>
                    </Box>
                </React.Fragment>
            );
            case 'Raw JSON': return (
                <React.Fragment>
                    <Box
                        p={1}
                        display="flex"
                        style={{ paddingBottom: 0 }}
                        flexDirection="row-reverse">
                        <Box p={1}>
                            <Button variant="outlined"
                                aria-label="Save"
                                size="small"
                                title="Save to file"
                                onClick={handleSaveJson}
                                startIcon={<SaveAltIcon fontSize={'inherit'} />}>
                                Save
                            </Button>
                        </Box>
                        <Box p={1}>
                            <Button variant="outlined"
                                aria-label={jsonFormatted ? 'Raw' : 'Formatted'}
                                size="small"
                                onClick={handleJSONFormat}>
                                {jsonFormatted ? 'Raw' : 'Formatted'}
                            </Button>
                        </Box>
                    </Box>
                    <Box p={1} display="flex">
                        {jsonFormatted && <ReactJSON
                            src={v.data}
                            enableClipboard={false}
                            style={{ fontSize: '15px', width: '100%' }}
                            theme={theme.JsonViewerTheme}/>}
                        {!jsonFormatted && <pre
                            className={classes.rawJson}
                            style={{ fontSize: '15px' }}>
                            {JSON.stringify(v.data, null, 4)}
                        </pre>}
                    </Box>
                </React.Fragment>
            );
            default: return (
                <React.Fragment>
                    <Box
                        p={1}
                        display="flex"
                        flexDirection="row-reverse"
                        style={{ paddingBottom: 0 }}>
                        <Box p={1}>
                            <FormControlLabel
                                control={<Checkbox
                                    checked={showFavorites}
                                    onChange={handleShowFaboritesToggle}
                                    icon={<FavoriteBorder />}
                                    checkedIcon={<Favorite />} name="favorites" />}
                                label="Favorites"
                            />
                        </Box>
                        <Box p={1}>
                            <Input
                                value={search}
                                onChange={handleSearchChanged}
                                placeholder="Search..."
                                variant="outlined"
                                margin="dense"
                                style={{ margin: 0 }}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">
                                        <React.Fragment>
                                            <IconButton
                                                disabled={!search}
                                                aria-label="clear"
                                                title="clear"
                                                onClick={() => setSearch('')}
                                                size="large">
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                            <SearchIcon fontSize="inherit" />
                                        </React.Fragment>
                                    </InputAdornment>
                                }}
                            />
                        </Box>
                        <Box p={1} xs={2}
                            style={{ paddingLeft: 25 }}
                            className={classes.tagsToolbar}>
                            {metadataPerms.write &&
                                <IconButton
                                    aria-label="add tag"
                                    title="tag"
                                    onClick={() => setShowTags(true) }
                                    size="large">
                                    <BookmarkIcon fontSize="small" />
                                </IconButton>
                            }
                            {(v.data['bricata.tag'] || []).map((t, i) =>
                                <div key={t + i} className={classes.inlineTag}>
                                    <Tag
                                        tag={t}
                                        onUntag={() => handleUntag(v.data, t, k)}
                                        untagLabel={'untag'}/>
                                </div>
                            )}
                        </Box>
                    </Box>
                    <Box>
                        <TagSimpleList
                            show={showTags}
                            placement='bottom-end'
                            onClickAway={() => setShowTags(false)}
                            anchorEl={toolbarRef.current}
                            onSelect={t => handleTag(v.data, t, k)}/>
                    </Box>
                    <Box p={1} style={{ maxHeight: cheight }} className={classes.autoColumnList}>
                        <ul>
                            {Object.entries(v.data || {})
                                .filter(([k]) =>
                                    !skip(k) &&
                                    (!showFavorites || isFavorite(k)) &&
                                    (search.length < 1 ||
                                     k.toLowerCase().includes(search.toLowerCase()) ||
                                     (getTitle(k) || '').toLowerCase().includes(search.toLowerCase())))
                                .sort((a, b) => getSort(a[0]) - getSort(b[0]))
                                .map(([k, v], i) =>
                                    <li key={`${k}_${i}`}>
                                        <KeyValuePair
                                            path={k}
                                            data={getValue(k, v)}
                                            title={getTitle(k)}
                                            icon={getTitleIcon(k)}
                                            tip={getMappedTip(k)}
                                            titleTip={k}
                                            type={getType(k)}
                                            indexed={isIndexed(k)}
                                            ip={isIP(k)}
                                            fileHash={isFileHash(k)}
                                            country={getCountry(k)}
                                            sensor={_get(props.mtdt, '_source.bricata.sensor_uuid')}
                                            favorite={isFavorite(k)}
                                            onFavorite={() => handleToggleFavorite(k)}
                                            onClick={isIndexed(k) && props.onFilter
                                                ? (e, v) => handleOnValueClick(e, k, v)
                                                : undefined
                                            }/>
                                    </li>
                                )}
                        </ul>
                    </Box>
                </React.Fragment>
            );
        }
    }, [props]);

    // resize hook
    useEffect(() => {
        const profile = getProfile('metadata');
        setShowFavorites(_get(profile, 'favorites.show') || false);
        setFavorites(_get(profile, 'favorites.selections') || {});
    }, [getProfile]);

    // size hook
    useEffect(() => {
        const top = 210;
        setCHeight(Math.max(height - props.header - top, 82));
    }, [height, props.header]);

    // show something on the page on init
    useEffect(() => {
        if (!props.mtdt) return;

        // get page going while it's looking up other connections
        setTabs({
            summary: {
                order: 1,
                data: Flatten({ object: props.mtdt._source, delimiter: '.' })
            }
        });
    }, [props.mtdt, props.cmc]);

    useEffect(() => {
        if (backtesting) {
            setTabs(current => {
                return {
                    ...current,
                    // backtesting is always next to last
                    Backtesting: { data: backtesting, order: 111 },
                };
            });
        }
    }, [backtesting]);

    useEffect(() => {
        if (!props.mtdt) return;
        const btuuid = GetBTUUID(props.mtdt, '_source.bt_uuid');
        const readMtdt = async () => {
            const backtesting = props.isBT || (btuuid || '').length ? 1 : 0;
            const ts = backtesting
                ? _get(props.mtdt, '_source.bro_log.orig_ts')
                : _get(props.mtdt, '_source.timestamp');
            const start = Moment(ts).subtract(11, 'minute').utc().toISOString();
            const end = Moment(ts).add(11, 'minute').utc().toISOString();
            MetadataService.getConnections({
                uuid: uuid(props.mtdt._source),
                start,
                end,
                backtesting,
                btuuid,
            }).then(m => {
                if (!m) return;

                // sort by file_name and then by UUID for consistent tag order
                const metadatas = (m.metadatas || []).sort((a, b) => {
                    if (a.file_name === b.file_name) {
                        if (uuid(a) < uuid(b)) return -1;
                        return 1;
                    } else if (a.file_name < b.file_name) return -1;
                    return 1;
                });

                // slap everything together for a summary view
                const summary = metadatas.reduce((a, c) => {
                    return _mergeWith(a, c, MtdtMerge);
                }, _cloneDeep(props.mtdt._source));

                fetchAssociatedAlerts(summary);

                // finally set tabs
                const names = [];
                let i = 0;
                const flat = Flatten({ object: summary, delimiter: '.' });
                const tabs = {
                    summary: {
                        order: i,
                        data: flat
                    }
                };

                disambiguate({ object: summary, delimiter: '.' });

                // add the sea urchin if we need one
                const origh = _get(summary, 'bro_log', {})['id.orig_h'];
                if (origh) {
                    tabs['Activity Flow'] = {
                        data: { ip: origh, country: _get(summary, 'bricata.src_location.country') },
                        order: i++
                    };
                }
                metadatas.forEach(s => {
                    const tname = getUniqueName(_get(s, 'bricata.event_source', ''), names);
                    names.push(tname);
                    tabs[tname] = { order: i++, data: Flatten({ object: s, delimiter: '.' }) };
                });
                tabs['Raw JSON'] = {
                    data: { summaryData: summary, sources: metadatas },
                    order: 420, // raw json is always last
                };
                // occasionally the backtesting tab gets here first
                setTabs(current => {
                    return {
                        ...current,
                        ...tabs,
                    };
                });
            });
        };

        const fetchPolicy = async () => {
            if (!gatorPerms.read) return;

            const sensor = sensorNames[_get(props.mtdt, '_source.bricata.sensor_uuid')];
            const host = sensor ? sensor.host : _get(props.mtdt, '_source.bricata.sensor_ipv4');
            PolicyService.getCurrentPolicy('data_tags', host).then(gator => {
                setPolicy({ gator });
            });
        };

        const fetchBacktestingJob = async () => {
            if (!btuuid) return;
            BacktestingService.getJob(backtestingJobs[btuuid].name).then(d => {
                if (!_isEmpty(d)) {
                    const flat = Flatten({
                        object: d,
                        delimiter: '.'
                    });
                    setBacktesting(flat);
                }
            });
        };

        const fetchPcapSituation = async () => {
            const situation = { full: false, smart: false };
            if (!sensorPerms.read || !pcapPerms.read) {
                setPcapSituation(situation);
                return;
            }
            const data = _get(props.mtdt, '_source');
            if (!data || !data.bricata || !data.bro_log) {
                setPcapSituation(situation);
                return;
            }
            // is proto compliant?
            const c = PcapService.compliantProtocols
                .find(p => p === (data.bro_log.proto || '').toLowerCase());
            if (!c) {
                setPcapSituation(situation);
                return;
            }
            // is pcap running on the sensor?
            SensorsService.getApps(data.bricata.sensor_uuid, props.cmc).then(a => {
                // setApps(a || {});
                if (!(a || {}).captured) {
                    setPcapSituation(situation);
                    return;
                }

                // with metadata it's all or nothing
                setPcapSituation({ full: true, smart: true });
            });
        };

        readMtdt();
        fetchPolicy();
        fetchPcapSituation();
        fetchBacktestingJob();
    }, [
        props.mtdt,
        props.cmc,
        // permission objects are mutating causing rerenders.
        // presumably these will be static for the entire session
        // gatorPerms, sensorPerms,
        sensorNames,
        fetchAssociatedAlerts,
        backtestingJobs,
    ]);

    return (
        <Card className={classes.root}>
            <div ref={toolbarRef}></div>

            <Box p={2} className={classes.alertHeader}>
                <Box style={{ width: 300, minWidth: 300 }}>
                    <Typography variant="h5">Metadata Details</Typography>
                    {MC2CHECK && props.cmc &&
                        <span>CMC: <span
                            style={{
                                paddingRight: 10,
                                color: theme.palette.secondary.main
                            }}>{props.cmc.name}
                        </span> </span>
                    }
                    Sensor:
                    {props.onFilter && <Link
                        color="primary"
                        onClick={(e) => handleOnValueClick(e.currentTarget, 'bricata.sensor_uuid', _get(props.mtdt, '_source.bricata.sensor_uuid'))}
                        style={{ paddingLeft: 10, cursor: 'pointer' }}>
                        {<SensorName uuid={'' + _get(props.mtdt, '_source.bricata.sensor_uuid')} />}
                    </Link>}
                    {!props.onFilter && <span style={{ paddingLeft: 10 }}>
                        {<SensorName uuid={'' + _get(props.mtdt, '_source.bricata.sensor_uuid')} />}
                    </span>}
                </Box>

                <Box style={{ width: 120, minWidth: 120 }}>
                    <Fab
                        title="previous"
                        disabled={!props.onPrev}
                        onClick={props.onPrev}
                        size="small"
                        color={props.isBT ? 'backtesting' : 'primary'}
                        aria-label="previous"
                        className={classes.margin}>
                        <NavigateBeforeIcon/>
                    </Fab>
                    <Fab
                        title="next"
                        disabled={!props.onNext}
                        onClick={props.onNext}
                        size="small"
                        color={props.isBT ? 'backtesting' : 'primary'}
                        aria-label="next"
                        className={classes.margin}>
                        <NavigateNextIcon/>
                    </Fab>
                </Box>

                <Grid container justifyContent="flex-end" style={{ width: 'auto' }}>
                    <Grid item>
                        <LabeledToolbar>
                            {pcapPerms.read && (pcapSituation.full || pcapSituation.smart) &&
                                <PopupMenu
                                    tooltip="request pcap"
                                    aria-label="pcap"
                                    items={getPcapMenu()}
                                    onSelect={handlePcap}
                                    button={<Button size="small" startIcon={<FindInPageIcon/>}>
                                        <p>Pcap</p>
                                    </Button>}
                                    icon={<FindInPageIcon fontSize="small" />}
                                    placement={'bottom-end'}/>}
                            {alertsPerms.read && alertsLookup &&
                                <Button
                                    aria-label="Find Alerts"
                                    title="Find Alerts"
                                    size="small"
                                    startIcon={<HuntIcon />}
                                    onClick={handleFindAlerts}>
                                    <p>Hunt</p>
                                </Button>
                            }
                            {/* tune */}
                            {gatorPerms.read && gatorPerms.write && !MC2CHECK &&
                                <Button
                                    aria-label="tune"
                                    title="tune"
                                    size="small"
                                    startIcon={<TuneIcon />}
                                    onClick={handleTune}>
                                    <p>Tune</p>
                                </Button>
                            }
                            {/* share */}
                            <Button
                                aria-label="share link"
                                size="small"
                                startIcon={<ShareIcon />}
                                onClick={() => props.onShare({
                                    uuid: _get(props.mtdt, '_source.bricata.event_uuid')
                                })}
                                title="share link">
                                <p>Share</p>
                            </Button>
                        </LabeledToolbar>
                    </Grid>
                </Grid>
            </Box>

            <AppBar
                position="static"
                color={props.isBT ? 'backtesting' : 'primary'}
                enableColorOnDark={props.isBT}>
                <Tabs
                    value={tab}
                    onChange={handleTabChanged}
                    indicatorColor="primary"
                    textColor={props.isBT ? 'inherit' : 'primary'}
                    variant="scrollable"
                    scrollButtons="auto"
                    TabIndicatorProps={{
                        style: {
                            background: props.isBT ? theme.palette.backtesting.light : '',
                        }
                    }}>
                    {Object.entries(tabs || {})
                        .sort((a, b) => a[1].order - b[1].order)
                        .map(([k, v], i) =>
                            <Tab key={`${k}_${i}`} label={k}/>
                        )
                    }
                </Tabs>
            </AppBar>
            {Object.entries(tabs || {})
                .sort((a, b) => a[1].order - b[1].order)
                .map(([k, v], i) =>
                    <TabPanel key={`${k}_${i}`} current={tab} value={getTabIndex(k)}>
                        {renderTab(k, v)}
                    </TabPanel>
                )
            }
            <Popover
                open={Boolean(valuePopup)}
                anchorEl={valuePopup ? valuePopup.elm : null}
                onClose={() => setValuePopup()}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}>
                <StyledPaper className={classes.paper}>
                    <MenuItem onClick={handleOnFilter}>
                        <ListItemIcon className={classes.itemIcon}>
                            <SearchIcon fontSize='small'/>
                        </ListItemIcon>
                        Filter
                    </MenuItem>
                </StyledPaper>
            </Popover>
        </Card>
    );
};

MetadataDetail.propTypes = {
    mtdt: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object
    ]),
    onTag: PropTypes.func,
    onUntag: PropTypes.func,
    onPrev: PropTypes.func,
    onNext: PropTypes.func,
    onFilter: PropTypes.func,
    onShare: PropTypes.func,
    onTune: PropTypes.func,
    params: PropTypes.object,
    header: PropTypes.number,
    cmc: PropTypes.object,
    isBT: PropTypes.bool,
};

export default MetadataDetail;
